import { useState, useEffect, useCallback, useRef } from 'react';
import { GameQuestionManager, Question, GameConfig } from '../services/GameQuestionManager';
import { FirestoreQuestionProgressPersistence } from '../services/QuestionProgressPersistence';

interface UseGameQuestionsOptions {
  gameType: GameConfig['gameType'];
  language?: GameConfig['language'];
  season?: GameConfig['season'];
  questionsPerSet?: number;
  prefetchAtQuestion?: number;
  autoInitialize?: boolean;
  userId?: string | null;
  isVisible?: boolean;
}

interface UseGameQuestionsReturn {
  // State
  currentQuestion: Question | null;
  remainingQuestions: number;
  isLoading: boolean;
  error: string | null;
  stats: {
    currentQuestions: number;
    usedQuestions: number;
    prefetchedQuestions: number;
    currentBlock: number;
    totalBlocks: number;
    nextPrefetchBlock: number | null;
    prefetchAt: number;
  };

  // Actions
  getNextQuestion: () => Question | null;
  initialize: () => Promise<void>;
  resetUsedQuestions: () => void;
  forceReload: () => Promise<void>;
  
  // Manager instance (for advanced usage)
  manager: GameQuestionManager | null;
}

/**
 * React hook for managing game questions with CDN loading
 */
export function useGameQuestions(options: UseGameQuestionsOptions): UseGameQuestionsReturn {
  const {
    gameType,
    language = 'en',
    season = 'v1',
    questionsPerSet = 10,
    prefetchAtQuestion = 8,
    autoInitialize = true,
    userId = null,
    isVisible = true
  } = options;

  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [remainingQuestions, setRemainingQuestions] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    currentQuestions: 0,
    usedQuestions: 0,
    prefetchedQuestions: 0,
    currentBlock: 0,
    totalBlocks: 0,
    nextPrefetchBlock: null as number | null,
    prefetchAt: 8
  });

  const managerRef = useRef<GameQuestionManager | null>(null);

  // Singleton pattern: ensure only one manager per game type exists
  useEffect(() => {
    // Create a unique key that doesn't include userId to prevent recreation for anonymous users
    const cfgKey = `${gameType}|${language}|${season}|${questionsPerSet}|${prefetchAtQuestion}`;
    const currentKey = (managerRef.current as any)?.__configKey;
    
    if (!managerRef.current || currentKey !== cfgKey) {
      console.log(`🎮 Creating new GameQuestionManager for ${gameType} (config changed or first time)`);
      const persistence = userId ? new FirestoreQuestionProgressPersistence(userId) : undefined;
      managerRef.current = new GameQuestionManager({
        gameType,
        language,
        season,
        questionsPerSet,
        prefetchAtQuestion,
        userId
      }, persistence);
      (managerRef.current as any).__configKey = cfgKey;
    } else {
      console.log(`🎮 Reusing existing GameQuestionManager for ${gameType}`);
    }
  }, [gameType, language, season, questionsPerSet, prefetchAtQuestion]); // userId intentionally excluded to prevent recreation

  // Initialize the question manager
  const initialize = useCallback(async () => {
    if (!managerRef.current) return;

    // Don't re-initialize if already initialized
    if (managerRef.current.isInitialized()) {
      console.log(`🎮 ${gameType} manager already initialized, skipping`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(`🎮 Initializing ${gameType} questions...`);
      
      const questions = await managerRef.current.initialize();
      
      // Set initial question
      const firstQuestion = managerRef.current.getCurrentQuestion();
      setCurrentQuestion(firstQuestion);
      setRemainingQuestions(managerRef.current.getRemainingQuestions());
      setStats(managerRef.current.getStats());
      
      console.log(`✅ Initialized with ${questions.length} questions`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize questions';
      setError(errorMessage);
      console.error('❌ Failed to initialize questions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-initialize only when game card is visible (after spin)
  useEffect(() => {
    if (autoInitialize && managerRef.current && isVisible) {
      console.log(`🎮 [${gameType}] Auto-initialize triggered - isInitialized: ${managerRef.current.isInitialized()}, remainingQuestions: ${managerRef.current.getRemainingQuestions()}`);
      
      // Check if manager needs initialization (either not initialized or no questions available)
      if (!managerRef.current.isInitialized() || managerRef.current.getRemainingQuestions() === 0) {
        console.log(`🎮 [${gameType}] Manager needs initialization, calling initialize()`);
        initialize();
      } else {
        // Manager is already initialized and has questions, just update the UI state
        const firstQuestion = managerRef.current.getCurrentQuestion();
        setCurrentQuestion(firstQuestion);
        setRemainingQuestions(managerRef.current.getRemainingQuestions());
        setStats(managerRef.current.getStats());
        console.log(`🎮 [${gameType}] Manager already initialized with ${managerRef.current.getRemainingQuestions()} questions, reusing`);
      }
    }
  }, [autoInitialize, isVisible, initialize]);

  // Get next question
  const getNextQuestion = useCallback((): Question | null => {
    if (!managerRef.current) return null;

    const beforeStats = managerRef.current.getStats();
    console.log(`🎮 [${gameType}] getNextQuestion called - before: ${beforeStats.currentQuestions} questions, ${beforeStats.usedQuestions} used`);

    const question = managerRef.current.getNextQuestion();
    
    if (question) {
      const afterStats = managerRef.current.getStats();
      console.log(`🎮 [${gameType}] getNextQuestion success - after: ${afterStats.currentQuestions} questions, ${afterStats.usedQuestions} used`);
      setCurrentQuestion(question);
      setRemainingQuestions(managerRef.current.getRemainingQuestions());
      setStats(managerRef.current.getStats());
    } else {
      console.log(`🎮 [${gameType}] getNextQuestion failed - no question returned`);
    }

    return question;
  }, []);

  // Reset used questions
  const resetUsedQuestions = useCallback(() => {
    if (managerRef.current) {
      managerRef.current.resetUsedQuestions();
      setStats(managerRef.current.getStats());
    }
  }, []);

  // Force reload questions
  const forceReload = useCallback(async () => {
    if (!managerRef.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const questions = await managerRef.current.forceReload();
      const firstQuestion = managerRef.current.getCurrentQuestion();
      
      setCurrentQuestion(firstQuestion);
      setRemainingQuestions(managerRef.current.getRemainingQuestions());
      setStats(managerRef.current.getStats());
      
      console.log(`🔄 Reloaded ${questions.length} questions`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reload questions';
      setError(errorMessage);
      console.error('❌ Failed to reload questions:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);



  // Update stats periodically
  useEffect(() => {
    if (!managerRef.current) return;

    const updateStats = () => {
      setStats(managerRef.current!.getStats());
    };

    const interval = setInterval(updateStats, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  return {
    // State
    currentQuestion,
    remainingQuestions,
    isLoading,
    error,
    stats,

    // Actions
    getNextQuestion,
    initialize,
    resetUsedQuestions,
    forceReload,

    // Manager instance
    manager: managerRef.current
  };
}

// Convenience hooks for specific game types
export function useTriviaQuestions(language?: GameConfig['language'], userId?: string | null, isVisible?: boolean) {
  return useGameQuestions({ gameType: 'trivia', language, userId, isVisible });
}

export function useWouldYouRatherQuestions(language?: GameConfig['language'], userId?: string | null, isVisible?: boolean) {
  return useGameQuestions({ gameType: 'wouldYouRather', language, userId, isVisible });
}

export function useWhichOneYouAreQuestions(language?: GameConfig['language'], userId?: string | null, isVisible?: boolean) {
  return useGameQuestions({ gameType: 'whichOneYouAre', language, userId, isVisible });
}

// Removed useDareOrTruthQuestions - use separate useTruthQuestions and useDareQuestions instead

// Separate hooks for Truth and Dare
export function useTruthQuestions(language?: GameConfig['language'], userId?: string | null, isVisible?: boolean) {
  return useGameQuestions({ gameType: 'truth', language, userId, isVisible, autoInitialize: false });
}

export function useDareQuestions(language?: GameConfig['language'], userId?: string | null, isVisible?: boolean) {
  return useGameQuestions({ gameType: 'dare', language, userId, isVisible, autoInitialize: false });
}
